import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { getAssignableRoleOptions } from './roleDropdownUtils'

import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { ROLE_LABELS } from '@/constants/enterprise'
import { selectCurrentEnterprise } from '@/store/features/user/userSlice'
import { PlanType, Role } from '@/types'
import { getPlanTypeFromProductName } from '@/utils/subscription'

export const roleTitle = ROLE_LABELS

const getRoleHelperText = (role: Role, planName: string): string => {
  if (role === 'member') {
    return `Access to ${planName} but can't manage members or billing`
  }
  if (role === 'it_admin') {
    return `Manages members, billing, and company settings without consuming a paid ${planName} seat`
  }
  return `${planName} plus the ability to manage members, billing, and company settings`
}

interface RoleDropdownProps {
  currentRole: Role
  changeRole: (role: Role) => void
  selfEdit?: boolean
}

export const RoleDropdown = (props: RoleDropdownProps) => {
  const { currentRole, changeRole, selfEdit = false } = props

  const enterprise = useSelector(selectCurrentEnterprise)
  const productName = enterprise?.subscription?.productName
  const planType = productName ? getPlanTypeFromProductName(productName) : null

  // Handle all plan types explicitly including legacy Team plans
  const planName =
    planType === PlanType.Enterprise ? 'Flow Enterprise' : planType === PlanType.Team ? 'Flow Teams' : 'Flow Pro'

  const roleItems = useMemo<DropdownItem[]>(
    () =>
      getAssignableRoleOptions(selfEdit).map((option) => ({
        id: option.value,
        title: option.label,
        helperText: getRoleHelperText(option.value as Role, planName),
        selected: option.value === currentRole,
      })),
    [planName, currentRole, selfEdit]
  )

  const handleSelect = (items: DropdownItem[]) => {
    const newRole = items[0].id as Role
    changeRole(newRole)
  }

  return (
    <Dropdown
      items={roleItems}
      setItems={() => {}} // Selection is managed via handleSelect
      searchable={false}
      onSelect={handleSelect}
    />
  )
}
